import React, { useMemo, useState } from 'react'
import { Form, InputNumber, Select, Input, Tag, Tabs, Alert } from 'antd'
import { Cloud, FolderOpen, Info } from 'lucide-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { sapApi, bulkPodApi, podBulkUploadApi } from '@/api/services'
import { uploadFileViaPresignedPut } from '@/utils/uploadFileToS3'
import {
  clearPersistedBatchRun,
  fetchBulkUploadResumeStatus,
  folderKeyFromFiles,
  groupFilesByDocumentNo,
  loadPersistedBatchId,
  persistBatchRun,
  type BulkUploadResumeStatus,
} from '@/utils/bulkUploadResume'
import { runBulkUploadPipeline, type PipelineProgress } from '@/utils/bulkUploadPipeline'
import { clearActiveRun, loadActiveRun } from '@/utils/bulkUploadRunSession'
import type { ApiResponse, BulkPodJob } from '@/types'
import { toast } from 'sonner'
import { Button } from '@/components/ui/Button'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '@/stores/authStore'
import BulkUploadHistoryPanel from './BulkUploadHistoryPanel'
import BulkUploadResultsPanel from './BulkUploadResultsPanel'
import BulkUploadKpiGrid, { type BulkUploadKpiItem } from './BulkUploadKpiGrid'
import BulkUploadResumeSummary from './BulkUploadResumeSummary'
import BulkFilePicker from './BulkFilePicker'
import BulkUploadProgress from './BulkUploadProgress'
import { PageShell, PageHero, DashboardPanel } from '@/components/dashboard'
import { Layers } from 'lucide-react'

const { Option } = Select
const { TextArea } = Input

const TERMINAL = new Set(['completed', 'failed', 'cancelled'])

type RunPhase = 'idle' | 'pipeline' | 'processing' | 'complete'

const CooldownCountdown: React.FC<{ endsAt: string | null }> = ({ endsAt }) => {
  const [timeLeft, setTimeLeft] = useState('15:00')

  React.useEffect(() => {
    if (!endsAt) return
    const updateTimer = () => {
      const diff = new Date(endsAt).getTime() - Date.now()
      if (diff <= 0) {
        setTimeLeft('00:00')
        return
      }
      const mins = Math.floor(diff / 60000)
      const secs = Math.floor((diff % 60000) / 1000)
      setTimeLeft(`${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`)
    }
    updateTimer()
    const interval = setInterval(updateTimer, 1000)
    return () => clearInterval(interval)
  }, [endsAt])

  return <span className="font-mono tabular-nums">{timeLeft}</span>
}

const BulkPodUploadPage: React.FC = () => {
  const qc = useQueryClient()
  const { user } = useAuthStore()
  const canBulkUpload = user?.permissions?.includes('pod.bulk_upload') ?? false
  const { t } = useTranslation(['BulkPodUpload', 'Common'])
  const [form] = Form.useForm()
  const documentTypeWatch = Form.useWatch('documentType', form) || 'ALT'
  const [activeBatchId, setActiveBatchId] = useState<string | null>(null)
  const [batchRolledBack, setBatchRolledBack] = useState(false)
  const [activeJobId, setActiveJobId] = useState<number | null>(null)
  const [activeTab, setActiveTab] = useState('local')

  const [selectedFiles, setSelectedFiles] = useState<File[]>([])
  const [runPhase, setRunPhase] = useState<RunPhase>('idle')
  const [processingProgress, setProcessingProgress] = useState<PipelineProgress | null>(null)
  const [processResult, setProcessResult] = useState<{
    totalProcessed: number
    approvedUpdated: number
    approvedInserted: number
    skippedApproved: number
    failed: number
    attachmentsCreated: number
  } | null>(null)

  const [cooldownState, setCooldownState] = useState<{ coolingDown: boolean; endsAt: string | null }>({
    coolingDown: false,
    endsAt: null,
  })
  const [runTimer, setRunTimer] = useState<{ startedAt: number; endedAt: number | null } | null>(null)

  const [interruptedRun, setInterruptedRun] = useState(() => loadActiveRun())

  const folderInputRef = React.useRef<HTMLInputElement>(null)
  const pollIntervalRef = React.useRef<ReturnType<typeof setInterval> | null>(null)
  const abortRunRef = React.useRef(false)

  const requestAbort = () => {
    abortRunRef.current = true
  }

  const clearPollInterval = () => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current)
      pollIntervalRef.current = null
    }
  }

  React.useEffect(() => () => clearPollInterval(), [])

  const isRunActive = runPhase === 'pipeline'

  const resumeFolderKey = useMemo(() => folderKeyFromFiles(selectedFiles), [selectedFiles])
  const resumeFileNames = useMemo(() => selectedFiles.map(f => f.name), [selectedFiles])

  const resumePrefetchQuery = useQuery({
    queryKey: ['bulk-upload-resume-prefetch', documentTypeWatch, resumeFolderKey],
    queryFn: () => fetchBulkUploadResumeStatus(documentTypeWatch, resumeFileNames),
    enabled: selectedFiles.length > 0 && !isRunActive,
    staleTime: 60_000,
  })

  const startPollingQueue = (existingToastId?: string, pollBatchId?: string | null) => {
    clearPollInterval()
    setRunPhase('processing')
    const toastId =
      existingToastId ||
      toast.loading(t('BulkPodUpload:messages.polling', 'Processing POD queue on the server…'))
    const batchForPoll = pollBatchId ?? activeBatchId

    let pollAttempts = 0
    const MAX_POLL_ATTEMPTS = 48000

    pollIntervalRef.current = setInterval(async () => {
      try {
        pollAttempts++
        const statusRes: any = await podBulkUploadApi.queueStatus(
          batchForPoll ? { batchId: batchForPoll } : undefined
        )
        const job = statusRes?.data?.job
        const queueCounts = statusRes?.data?.queue
        const batchSummary = statusRes?.data?.batchSummary

        setBatchRolledBack(!!job?.rolledBack)
        setCooldownState({
          coolingDown: !!job?.coolingDown,
          endsAt: job?.cooldownEndsAt || null,
        })

        const processed =
          (queueCounts?.COMPLETED ?? 0) + (queueCounts?.SKIPPED ?? 0) + (queueCounts?.FAILED ?? 0)
        const pending = (queueCounts?.PENDING ?? 0) + (queueCounts?.PROCESSING ?? 0)
        const batchTotal = Number(batchSummary?.totalFiles ?? processed + pending)

        if (batchTotal > 0) {
          setProcessingProgress({
            current: processed,
            total: batchTotal,
            percent: Math.round((processed / batchTotal) * 100),
            currentDocumentNo: null,
            succeededDocs: queueCounts?.COMPLETED ?? 0,
            failedDocs: queueCounts?.FAILED ?? 0,
            skippedDocs: queueCounts?.SKIPPED ?? 0,
          })
        }

        if (job?.coolingDown) {
          toast.loading(
            t('BulkPodUpload:messages.cooldownToast', 'Cooldown active — {{processed}} done, {{pending}} remaining', {
              processed,
              pending,
            }),
            { id: toastId }
          )
        } else {
          toast.loading(
            t('BulkPodUpload:messages.processingToast', '{{processed}} done, {{pending}} remaining', {
              processed,
              pending,
            }),
            { id: toastId }
          )
        }

        qc.invalidateQueries({ queryKey: ['bulk-upload-batch-status', batchForPoll] })
        qc.invalidateQueries({ queryKey: ['bulk-upload-queue-items', batchForPoll] })

        const workerFinished = job?.done === true || (!job?.running && pending === 0 && pollAttempts >= 2)
        const timedOut = pollAttempts >= MAX_POLL_ATTEMPTS

        if (workerFinished || timedOut) {
          clearPollInterval()
          setRunPhase('complete')
          setCooldownState({ coolingDown: false, endsAt: null })
          setRunTimer(prev => (prev ? { ...prev, endedAt: Date.now() } : null))

          if (timedOut && pending > 0) {
            toast.error(t('BulkPodUpload:messages.processTimeout', 'Processing is taking longer than expected — refresh to check status'), {
              id: toastId,
            })
          } else if (job?.error) {
            toast.error(job.error, { id: toastId })
          } else if (batchTotal === 0 && pollAttempts >= 3) {
            toast.error(
              t(
                'BulkPodUpload:messages.emptyBatch',
                'No files were registered for this batch — try uploading again or contact support'
              ),
              { id: toastId }
            )
          } else if (job?.summary || batchSummary) {
            setProcessResult(job?.summary ?? batchSummary)
            toast.success(t('BulkPodUpload:messages.processComplete', 'Bulk upload completed'), { id: toastId })
            setSelectedFiles([])
          } else {
            toast.success(t('BulkPodUpload:messages.processComplete', 'Bulk upload completed'), { id: toastId })
          }
        }
      } catch (pollErr) {
        console.error('Poll error during background queue processing:', pollErr)
      }
    }, 4000)
  }

  React.useEffect(() => {
    const checkActiveJob = async () => {
      try {
        const res: any = await podBulkUploadApi.queueStatus()
        const job = res?.data?.job
        if (job?.running) {
          if (job.batchId) setActiveBatchId(job.batchId)
          setRunTimer(prev => prev ?? { startedAt: Date.now(), endedAt: null })
          setRunPhase('processing')
          toast.info(t('BulkPodUpload:messages.reconnecting', 'Reconnecting to active upload job…'))
          startPollingQueue(undefined, job.batchId)
        } else if (job?.done && job.summary && !processResult) {
          setProcessResult(job.summary)
          if (job.batchId) setActiveBatchId(job.batchId)
        }
        if (job?.rolledBack) setBatchRolledBack(true)
      } catch (err) {
        console.error('Error checking active background job on mount:', err)
      }
    }
    checkActiveJob()
  }, [])

  React.useEffect(() => {
    if (folderInputRef.current) {
      folderInputRef.current.setAttribute('webkitdirectory', '')
      folderInputRef.current.setAttribute('directory', '')
    }
  }, [])

  const formatJobStatus = (s: string) => {
    const colors: Record<string, string> = {
      pending: 'default',
      listing: 'processing',
      running: 'processing',
      completed: 'success',
      failed: 'error',
      cancelled: 'default',
    }
    const label =
      s === 'completed'
        ? t('Common:general.completed', 'Completed')
        : s === 'failed'
          ? t('BulkPodUpload:job.failed', 'Failed')
          : s === 'pending'
            ? t('Common:general.open', 'Open')
            : s
    return <Tag color={(colors[s] || 'default') as 'default'}>{label}</Tag>
  }

  const { data: documentTypes } = useQuery({
    queryKey: ['document-types'],
    queryFn: () => sapApi.getDocumentTypes(),
    select: (response: any) => {
      const payload = response?.data
      if (Array.isArray(payload)) return payload
      if (Array.isArray(payload?.data)) return payload.data
      return []
    },
  })

  const docTypeOptions = (Array.isArray(documentTypes) ? documentTypes : []).map((type: any) => (
    <Option key={type.code} value={type.code} disabled={type.disabled}>
      {type.name}
    </Option>
  ))

  const createMutation = useMutation({
    mutationFn: (values: { driveUrl: string; documentType: string; delayMs: number }) =>
      bulkPodApi.createJob(values).then((res: any) => res.data as ApiResponse<BulkPodJob>),
    onSuccess: payload => {
      if (!payload?.success || !payload.data?.id) {
        toast.error(payload?.error || t('BulkPodUpload:messages.startFailed', 'Failed to start job'))
        return
      }
      toast.success(t('BulkPodUpload:messages.startSuccess', 'Bulk POD job started'))
      setActiveJobId(Number(payload.data.id))
      qc.invalidateQueries({ queryKey: ['bulk-pod-job', payload.data.id] })
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.error || err?.message || t('BulkPodUpload:messages.startFailed', 'Failed to start job'))
    },
  })

  const jobQuery = useQuery({
    queryKey: ['bulk-pod-job', activeJobId],
    queryFn: async () => {
      if (!activeJobId) throw new Error('no job')
      const res = (await bulkPodApi.getJob(activeJobId)) as any
      return res?.data as ApiResponse<BulkPodJob>
    },
    enabled: Number.isFinite(activeJobId!) && (activeJobId ?? 0) > 0,
    refetchInterval: query => {
      const body = query.state.data as ApiResponse<BulkPodJob> | undefined
      const st = body?.data?.status
      return st && !TERMINAL.has(String(st)) ? 2700 : false
    },
  })

  const job = jobQuery.data?.data

  const cloudJobKpiItems: BulkUploadKpiItem[] = job
    ? [
        { key: 'queued', labelKey: 'job.totalQueued', fallback: 'Queued', value: job.totalFiles ?? 0, tone: 'default' },
        { key: 'processed', labelKey: 'job.processed', fallback: 'Processed', value: job.processed ?? 0, tone: 'blue' },
        { key: 'succeeded', labelKey: 'job.succeeded', fallback: 'Succeeded', value: job.succeeded ?? 0, tone: 'emerald' },
        { key: 'failed', labelKey: 'job.failed', fallback: 'Failed', value: job.failed ?? 0, tone: 'red' },
      ]
    : []

  const handleStart = async () => {
    const values = (await form.validateFields()) as {
      driveUrl: string
      documentType: string
      delayMs?: number
    }
    await createMutation.mutateAsync({
      driveUrl: values.driveUrl.trim(),
      documentType: values.documentType,
      delayMs: typeof values.delayMs === 'number' ? values.delayMs : 350,
    })
  }

  const handleFolderSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files) return

    const fileArray = Array.from(files)
    const allowedExtensions = new Set(['pdf', 'png', 'jpg', 'jpeg'])
    const filtered = fileArray.filter(f => {
      const ext = f.name.split('.').pop()?.toLowerCase() || ''
      return allowedExtensions.has(ext)
    })

    setSelectedFiles(filtered)
    setProcessingProgress(null)
    setProcessResult(null)
    setRunTimer(null)
    setRunPhase('idle')
    qc.removeQueries({ queryKey: ['bulk-upload-resume-prefetch'] })

    if (fileArray.length === 0) {
      toast.error(t('BulkPodUpload:messages.emptyFolder', 'Folder is empty or permission was denied'))
    } else if (filtered.length === 0) {
      const foundExtensions = Array.from(
        new Set(fileArray.map(f => f.name.split('.').pop()?.toLowerCase() || 'no-extension'))
      )
        .slice(0, 5)
        .join(', ')
      toast.error(
        t('BulkPodUpload:messages.unsupportedFiles', 'No supported files found. Extensions seen: {{ext}}', {
          ext: foundExtensions,
        })
      )
    } else {
      toast.success(
        t('BulkPodUpload:messages.foundFiles', 'Found {{count}} supported files', { count: filtered.length })
      )
    }
  }

  const uploadSingleFileToS3 = async (file: File): Promise<string | null> => {
    try {
      return await uploadFileViaPresignedPut(file)
    } catch (err) {
      console.error('Error uploading file to S3:', err)
      return null
    }
  }

  const handleUploadAndProcess = async () => {
    if (selectedFiles.length === 0) return

    const documentType = form.getFieldValue('documentType') || 'ALT'
    const folderKey = folderKeyFromFiles(selectedFiles)
    const fileNames = selectedFiles.map(f => f.name)

    let resume: BulkUploadResumeStatus
    try {
      resume = await qc.fetchQuery({
        queryKey: ['bulk-upload-resume-prefetch', documentType, folderKey],
        queryFn: () => fetchBulkUploadResumeStatus(documentType, fileNames),
        staleTime: 30_000,
      })
    } catch {
      toast.error(t('BulkPodUpload:messages.processedCheckFailed', 'Could not check processed files'))
      return
    }

    const batchId =
      loadPersistedBatchId(documentType, folderKey) ||
      resume.suggestedBatchId ||
      crypto.randomUUID()

    const docGroups = groupFilesByDocumentNo(selectedFiles)
    const toProcess = Array.from(docGroups.entries()).filter(([, files]) =>
      files.some(f => !resume.files[f.name]?.skip)
    ).length

    setActiveBatchId(batchId)
    setBatchRolledBack(false)
    setRunTimer({ startedAt: Date.now(), endedAt: null })
    setRunPhase('pipeline')
    setProcessResult(null)
    abortRunRef.current = false
    setInterruptedRun(null)

    if (resume.counts.done > 0 && toProcess > 0) {
      toast.info(
        t('BulkPodUpload:messages.resuming', '{{done}} already complete — continuing {{remaining}} documents', {
          done: resume.counts.done,
          remaining: toProcess,
        })
      )
    }

    setProcessingProgress({
      current: 0,
      total: docGroups.size,
      percent: 0,
      currentDocumentNo: null,
      succeededDocs: 0,
      failedDocs: 0,
      skippedDocs: 0,
    })

    try {
      const result = await runBulkUploadPipeline({
        selectedFiles,
        documentType,
        batchId,
        folderKey,
        resume,
        uploadFile: uploadSingleFileToS3,
        shouldAbort: () => abortRunRef.current,
        onProgress: setProcessingProgress,
        onDocumentComplete: (bid, done) => {
          if (done % 5 === 0) {
            qc.invalidateQueries({ queryKey: ['bulk-upload-batch-status', bid] })
            qc.invalidateQueries({ queryKey: ['bulk-upload-queue-items', bid] })
          }
        },
      })

      persistBatchRun(documentType, folderKey, batchId)
      qc.invalidateQueries({ queryKey: ['bulk-upload-batch-status', batchId] })
      qc.invalidateQueries({ queryKey: ['bulk-upload-queue-items', batchId] })
      qc.invalidateQueries({ queryKey: ['bulk-upload-history-stats'] })
      qc.invalidateQueries({ queryKey: ['bulk-upload-resume-prefetch'] })

      setRunPhase('complete')
      setRunTimer(prev => (prev ? { ...prev, endedAt: Date.now() } : null))

      if (result.aborted) {
        toast.warning(t('BulkPodUpload:messages.uploadCancelled', 'Upload stopped — re-select the same folder to continue'))
        clearActiveRun()
        persistBatchRun(documentType, folderKey, batchId)
      } else if (result.failedDocs > 0) {
        toast.warning(
          t('BulkPodUpload:messages.partialComplete', 'Finished with {{failed}} document errors — re-run the same folder to retry', {
            failed: result.failedDocs,
          })
        )
      } else if (toProcess === 0) {
        toast.info(t('BulkPodUpload:messages.allAlreadyDone', 'All files in this folder were already processed'))
      } else {
        toast.success(t('BulkPodUpload:messages.processComplete', 'Bulk upload completed'))
        setSelectedFiles([])
        clearActiveRun()
      }
    } catch (err: any) {
      persistBatchRun(documentType, folderKey, batchId)
      qc.invalidateQueries({ queryKey: ['bulk-upload-history-stats'] })
      qc.invalidateQueries({ queryKey: ['bulk-upload-resume-prefetch'] })
      toast.error(err?.response?.data?.error || err?.message || t('BulkPodUpload:messages.uploadFailed', 'Upload failed'))
      setRunPhase('complete')
      setRunTimer(prev => (prev ? { ...prev, endedAt: Date.now() } : null))
    }
  }

  const remainingToUpload =
    (resumePrefetchQuery.data?.counts.new ?? 0) + (resumePrefetchQuery.data?.counts.retry ?? 0)

  const startButtonLabel =
    runPhase === 'pipeline'
      ? t('BulkPodUpload:form.pipeline', 'Uploading & processing…')
      : remainingToUpload > 0 && (resumePrefetchQuery.data?.counts.done ?? 0) > 0
        ? t('BulkPodUpload:form.resumeUpload', 'Resume upload ({{count}} left)', { count: remainingToUpload })
        : t('BulkPodUpload:form.upload', 'Start upload')

  const renderLocalWorkflow = () => (
    <div className="space-y-6">
      <DashboardPanel
        title={t('BulkPodUpload:sections.configTitle', 'Configuration')}
        subtitle={t('BulkPodUpload:sections.configSubtitle', 'Document type applies to every file in this batch')}
      >
          <Form form={form} layout="vertical" initialValues={{ documentType: 'ALT' }} className="max-w-md">
            <Form.Item
              name="documentType"
              label={t('BulkPodUpload:form.docType', 'Document type (SAP)')}
              rules={[{ required: true, message: t('Common:errors.required', 'Required') }]}
            >
              <Select placeholder={t('BulkPodUpload:form.docTypePlaceholder', 'Select document type')}>
                {docTypeOptions}
              </Select>
            </Form.Item>
          </Form>
      </DashboardPanel>

      <DashboardPanel
        title={t('BulkPodUpload:sections.filesTitle', 'Select files')}
        subtitle={t('BulkPodUpload:sections.filesSubtitle', 'Filenames must match SAP document numbers (before the extension)')}
      >
          {canBulkUpload ? (
            <BulkFilePicker
              selectedCount={selectedFiles.length}
              folderInputRef={folderInputRef}
              onFolderSelect={handleFolderSelect}
              selectionLocked={isRunActive}
            />
          ) : (
            <div className="text-sm text-muted-foreground p-4 bg-muted/20 rounded-md border border-dashed border-border text-center">
              Folder selection and file upload are disabled in view-only mode.
            </div>
          )}
          {selectedFiles.length > 0 ? (
            <BulkUploadResumeSummary
              loading={resumePrefetchQuery.isFetching && !resumePrefetchQuery.data}
              error={resumePrefetchQuery.isError}
              resume={resumePrefetchQuery.data}
              onRetry={() => resumePrefetchQuery.refetch()}
            />
          ) : null}
          {interruptedRun && selectedFiles.length > 0 && resumeFolderKey === interruptedRun.folderKey ? (
            <Alert
              type="warning"
              showIcon
              className="mt-3"
              message={t('BulkPodUpload:messages.interruptedTitle', 'Previous upload was interrupted')}
              description={t(
                'BulkPodUpload:messages.interruptedBody',
                'Batch {{batchId}} had {{done}} of {{total}} documents processed. Click Resume to continue.',
                {
                  batchId: interruptedRun.batchId.slice(0, 8),
                  done: interruptedRun.processedDocs,
                  total: interruptedRun.totalDocs,
                }
              )}
            />
          ) : null}
      </DashboardPanel>

      <DashboardPanel
        title={t('BulkPodUpload:sections.runTitle', 'Run upload')}
        subtitle={t('BulkPodUpload:sections.runSubtitle', 'Each document uploads to storage, then SAP and approvals run before the next')}
      >
        <div className="space-y-4">
          {runTimer ? (
            <BulkUploadProgress
              current={processingProgress?.current ?? 0}
              total={processingProgress?.total ?? 0}
              percent={runPhase === 'complete' ? 100 : (processingProgress?.percent ?? 0)}
              phase={runPhase === 'idle' ? 'uploading' : runPhase === 'processing' ? 'processing' : runPhase}
              startedAt={runTimer.startedAt}
              endedAt={runTimer.endedAt}
              currentDocumentNo={processingProgress?.currentDocumentNo}
              succeededDocs={processingProgress?.succeededDocs}
              failedDocs={processingProgress?.failedDocs}
            />
          ) : null}

          <div className="flex flex-wrap items-center justify-end gap-2">
            {isRunActive ? (
              <Button type="button" variant="outline" onClick={requestAbort}>
                {t('BulkPodUpload:form.stop', 'Stop upload')}
              </Button>
            ) : null}
            <Button
              type="button"
              variant="outline"
              disabled={selectedFiles.length === 0 || isRunActive}
              onClick={() => {
                const documentType = form.getFieldValue('documentType') || 'ALT'
                clearPersistedBatchRun(documentType)
                clearActiveRun()
                setInterruptedRun(null)
                setSelectedFiles([])
                setProcessingProgress(null)
                setRunTimer(null)
                setRunPhase('idle')
                setActiveBatchId(null)
                qc.removeQueries({ queryKey: ['bulk-upload-resume-prefetch'] })
              }}
            >
              {t('Common:general.reset', 'Reset')}
            </Button>
            <Button
              type="button"
              onClick={handleUploadAndProcess}
              disabled={
                !canBulkUpload ||
                selectedFiles.length === 0 ||
                isRunActive ||
                (resumePrefetchQuery.isFetching && !resumePrefetchQuery.data)
              }
            >
              {startButtonLabel}
            </Button>
          </div>
        </div>
      </DashboardPanel>

      {cooldownState.coolingDown ? (
        <Alert
          type="warning"
          showIcon
          message={t('BulkPodUpload:cooldown.title', 'Rate limit cooldown')}
          description={
            <div className="flex flex-wrap items-center justify-between gap-3">
              <span>{t('BulkPodUpload:cooldown.description', 'Pausing to respect API limits. Processing resumes automatically.')}</span>
              <span className="rounded-md bg-background px-3 py-1 font-mono text-sm font-semibold">
                <CooldownCountdown endsAt={cooldownState.endsAt} />
              </span>
            </div>
          }
        />
      ) : null}

      {activeBatchId ? (
        <BulkUploadResultsPanel
          mode="local"
          batchId={activeBatchId}
          enabled
          isRunning={isRunActive}
          rolledBack={batchRolledBack}
          runTimer={runTimer}
          onResumeProcessing={() => startPollingQueue(undefined, activeBatchId)}
        />
      ) : null}
    </div>
  )

  const renderCloudWorkflow = () => (
    <div className="space-y-6">
      <DashboardPanel
        title={t('BulkPodUpload:sections.cloudTitle', 'Cloud folder import')}
        subtitle={t('BulkPodUpload:source.sourceSubtitle', 'Serial import runs on the server in document order')}
      >
          <Form
            layout="vertical"
            form={form}
            initialValues={{ delayMs: 350, documentType: 'ALT' }}
            className="max-w-3xl space-y-4"
          >
            <Form.Item
              name="driveUrl"
              label={t('BulkPodUpload:form.folderUrl', 'Folder / share URL')}
              rules={[
                {
                  required: true,
                  message: t('BulkPodUpload:form.folderUrlRequired', 'Paste Drive or SharePoint folder link'),
                },
              ]}
            >
              <TextArea
                disabled={!canBulkUpload}
                autoSize={{ minRows: 2 }}
                placeholder={t(
                  'BulkPodUpload:form.folderUrlPlaceholder',
                  'https://drive.google.com/drive/folders/… or SharePoint share link'
                )}
              />
            </Form.Item>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Form.Item
                name="documentType"
                label={t('BulkPodUpload:form.docType', 'Document type (SAP)')}
                rules={[{ required: true, message: t('Common:errors.required', 'Required') }]}
              >
                <Select disabled={!canBulkUpload} placeholder={t('BulkPodUpload:form.docTypePlaceholder', 'Select document type')}>
                  {docTypeOptions}
                </Select>
              </Form.Item>
              <Form.Item name="delayMs" label={t('BulkPodUpload:form.delay', 'Delay between files (ms)')}>
                <InputNumber disabled={!canBulkUpload} min={0} max={30_000} step={50} className="w-full" />
              </Form.Item>
            </div>

            <div className="flex flex-wrap gap-2">
              <Button type="button" onClick={handleStart} disabled={!canBulkUpload || createMutation.isPending}>
                {createMutation.isPending
                  ? t('BulkPodUpload:form.starting', 'Starting…')
                  : t('BulkPodUpload:form.start', 'Start bulk import')}
              </Button>
              {activeJobId ? (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => qc.invalidateQueries({ queryKey: ['bulk-pod-job', activeJobId] })}
                >
                  {t('BulkPodUpload:form.refresh', 'Refresh status')}
                </Button>
              ) : null}
            </div>
          </Form>
      </DashboardPanel>

      {activeJobId ? (
        <>
          <DashboardPanel
            title={t('BulkPodUpload:job.jobTitle', 'Job #{{id}}', { id: activeJobId })}
            subtitle={t('BulkPodUpload:job.jobSubtitle', 'Live counters from the backend')}
            actions={job?.status ? formatJobStatus(job.status) : undefined}
          >
              <div className="grid grid-cols-2 gap-4 text-sm sm:grid-cols-4">
                <div>
                  <div className="text-muted-foreground">{t('BulkPodUpload:job.provider', 'Provider')}</div>
                  <div className="mt-1 font-medium capitalize">{job?.detectedProvider?.replace('_', ' ') ?? '—'}</div>
                </div>
              </div>
              <BulkUploadKpiGrid items={cloudJobKpiItems} columns={4} />
              {job?.errorMessage ? (
                <Alert type="error" showIcon message={job.errorMessage} />
              ) : null}
          </DashboardPanel>

          <BulkUploadResultsPanel
            mode="cloud"
            jobId={activeJobId}
            enabled
            isRunning={job?.status ? !TERMINAL.has(String(job.status)) : false}
            rolledBack={!!job?.rolledBackAt}
          />
        </>
      ) : null}
    </div>
  )

  return (
    <PageShell>
      <PageHero
        title={t('BulkPodUpload:header.headerTitle', 'Bulk Upload POD')}
        subtitle={t(
          'BulkPodUpload:header.description',
          'Upload many POD files at once. Supported formats: PDF, PNG, JPG. Filenames must match SAP document numbers.',
        )}
        badges={[{ label: 'Bulk', accent: true, icon: Layers }]}
      />

      {!canBulkUpload && (
        <Alert
          type="warning"
          showIcon
          message="View-Only Mode"
          description="You have view-only access. Bulk uploads, file processing, and rollbacks are disabled."
        />
      )}

      <BulkUploadHistoryPanel
        onSelectLocalBatch={(batchId) => {
          setActiveBatchId(batchId)
          setActiveTab('local')
        }}
        onSelectCloudJob={(jobId) => {
          setActiveJobId(jobId)
          setActiveTab('cloud')
        }}
      />

      <Alert
        type="info"
        showIcon
        icon={<Info className="h-4 w-4" />}
        message={t('BulkPodUpload:requirements.title', 'Before you upload')}
        description={t(
          'BulkPodUpload:requirements.body',
          'Each filename (without extension) must be a valid SAP document number. PDF is preferred when multiple formats exist for the same document.'
        )}
      />

      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        className="leap-antd-tabs"
        items={[
          {
            key: 'local',
            label: (
              <span className="inline-flex items-center gap-2">
                <FolderOpen className="h-4 w-4" />
                {t('BulkPodUpload:tabs.local', 'Local folder')}
              </span>
            ),
          },
          {
            key: 'cloud',
            label: (
              <span className="inline-flex items-center gap-2">
                <Cloud className="h-4 w-4" />
                {t('BulkPodUpload:tabs.cloud', 'Google Drive / SharePoint')}
              </span>
            ),
          },
        ]}
      />

      {activeTab === 'local' ? renderLocalWorkflow() : renderCloudWorkflow()}
    </PageShell>
  )
}

export default BulkPodUploadPage
